import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminApi, OrderFilterParams } from '../api/adminApi';
import { Order, OrderStatus } from '../api/orderApi';
import { Filter, Calendar, User, DollarSign, RefreshCw, ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import CreateOrderModal from '../components/CreateOrderModal';
import OrderItemsModal from '../components/OrderItemsModal';

interface FilterState {
  searchTerm?: string;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  minAmount?: number;
  maxAmount?: number;
  customerEmail?: string;
  customerPhone?: string;
}

interface PaginationState {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export default function AdminOrdersPage() {
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<Order[]>([]);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [filters, setFilters] = useState<FilterState>({});
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    pageSize: 50,
    totalCount: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [showFilters, setShowFilters] = useState(false);
  const [updatingOrder, setUpdatingOrder] = useState<number | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showItemsModal, setShowItemsModal] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'Admin') {
      navigate('/login');
      return;
    }
    fetchOrders();
  }, [isAuthenticated, user, navigate, pagination.page, pagination.pageSize, filters]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params: OrderFilterParams = {
        page: pagination.page,
        pageSize: pagination.pageSize,
        // Основные параметры фильтрации
        searchTerm: filters.searchTerm,
        status: filters.status as OrderStatus,
        fromDate: filters.dateFrom,
        toDate: filters.dateTo,
        minTotalPrice: filters.minAmount,
        maxTotalPrice: filters.maxAmount,
        customerEmail: filters.customerEmail,
        customerPhone: filters.customerPhone,
        // Альтернативные параметры для совместимости с бэкендом
        search: filters.searchTerm,
        email: filters.customerEmail,
        phone: filters.customerPhone
      };
      
      const response = await adminApi.getAllOrders(params);
      if (response.success && response.data) {
        const ordersData = response.data.data || [];
        // Убеждаемся, что ordersData это массив
        setOrders(Array.isArray(ordersData) ? ordersData : []);
        setTotalPrice(response.data.totalPrice || 0);
        setPagination(prev => ({
          ...prev,
          totalCount: response.data?.totalCount || 0,
          totalPages: response.data?.totalPages || 0,
          hasNextPage: response.data?.hasNextPage || false,
          hasPreviousPage: response.data?.hasPreviousPage || false
        }));
      } else {
        setError(response.message || 'Ошибка при загрузке заказов');
      }
    } catch (err) {
      setError('Ошибка при загрузке заказов');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (orderId: number, newStatus: string) => {
    try {
      setUpdatingOrder(orderId);
      const response = await adminApi.updateOrderStatus(orderId, newStatus as OrderStatus);
      if (response.success) {
        setSuccess('Статус заказа обновлен');
        // Обновляем локальное состояние
        setOrders(prev => prev.map(order => 
          order.id === orderId ? { ...order, status: newStatus as OrderStatus } : order
        ));
      } else {
        setError(response.message || 'Ошибка при обновлении статуса заказа');
      }
    } catch (err) {
      setError('Ошибка при обновлении статуса заказа');
    } finally {
      setUpdatingOrder(null);
    }
  };

  const handleCreateOrderSuccess = () => {
    setSuccess('Заказ успешно создан');
    fetchOrders(); // Перезагружаем список заказов
  };

  const handleFilterChange = (field: keyof FilterState, value: string | number | undefined) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPagination(prev => ({ ...prev, page: 1 })); // Сбрасываем на первую страницу
  };

  const clearFilters = () => {
    setFilters({});
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPagination(prev => ({ ...prev, page: 1, pageSize: newPageSize }));
  };

  const getStatusColor = (status: string) => {
    const statusStr = String(status || '').toLowerCase();
    switch (statusStr) {
      case 'new':
        return 'bg-blue-100 text-blue-800';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      case 'shipping':
        return 'bg-purple-100 text-purple-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    const statusStr = String(status || '').toLowerCase();
    switch (statusStr) {
      case 'new':
        return 'Новый';
      case 'processing':
        return 'В обработке';
      case 'shipping':
        return 'Отправлен';
      case 'delivered':
        return 'Доставлен';
      case 'cancelled':
        return 'Отменен';
      default:
        return String(status || '');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ru-RU');
  };

  const calculateTotalAmount = () => {
    return orders.reduce((sum, order) => sum + order.totalPrice, 0);
  };

  if (loading) {
    return <div className="text-gray-300 mt-12">Загружаем информацию...</div>;
  }

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  return (
    <>
      <div className="flex w-0 flex-1 flex-col gap-4 py-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-xl font-semibold">Управление заказами</h1>
            <p className="text-sm text-gray-600 mt-1">
              Общая сумма за выбранный период: <span className="font-semibold">{totalPrice.toLocaleString('ru-RU')} ₽</span>
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Plus size={16} className="mr-2" />
            Создать заказ
          </button>
        </div>

        {success && (
          <div className="p-4 bg-green-100 border border-green-400 text-green-700 rounded">
            {success}
            <button
              onClick={() => setSuccess(null)}
              className="float-right text-green-800 hover:text-green-900"
            >
              ×
            </button>
          </div>
        )}

        {/* Фильтры */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-base font-medium">Фильтры</h2>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center text-gray-600 hover:text-gray-800"
            >
              <Filter size={16} className="mr-2" />
              {showFilters ? 'Скрыть фильтры' : 'Показать фильтры'}
            </button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Поиск</label>
                <input
                  type="text"
                  value={filters.searchTerm || ''}
                  onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                  placeholder="ID, ФИО, email..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Статус</label>
                <select
                  value={filters.status || ''}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Все статусы</option>
                  <option value="New">Новый</option>
                  <option value="Processing">В обработке</option>
                  <option value="Shipping">Отправлен</option>
                  <option value="Delivered">Доставлен</option>
                  <option value="Cancelled">Отменен</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Дата от</label>
                <input
                  type="date"
                  value={filters.dateFrom || ''}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Дата до</label>
                <input
                  type="date"
                  value={filters.dateTo || ''}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Мин. сумма</label>
                <input
                  type="number"
                  value={filters.minAmount || ''}
                  onChange={(e) => handleFilterChange('minAmount', e.target.value ? Number(e.target.value) : undefined)}
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Макс. сумма</label>
                <input
                  type="number"
                  value={filters.maxAmount || ''}
                  onChange={(e) => handleFilterChange('maxAmount', e.target.value ? Number(e.target.value) : undefined)}
                  placeholder="∞"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email клиента</label>
                <input
                  type="email"
                  value={filters.customerEmail || ''}
                  onChange={(e) => handleFilterChange('customerEmail', e.target.value)}
                  placeholder="email@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Телефон клиента</label>
                <input
                  type="tel"
                  value={filters.customerPhone || ''}
                  onChange={(e) => handleFilterChange('customerPhone', e.target.value)}
                  placeholder="+7 (999) 123-45-67"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}

          <div className="flex justify-between items-center">
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              Очистить фильтры
            </button>
            <button
              onClick={fetchOrders}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <RefreshCw size={16} className="mr-2" />
              Обновить
            </button>
          </div>
        </div>

        {/* Список заказов */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b">
            <div className="flex justify-between items-center">
                              <h2 className="text-base font-medium">Заказы ({pagination.totalCount})</h2>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">На странице:</span>
                <select
                  value={pagination.pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="px-2 py-1 border border-gray-300 rounded text-sm pr-8"
                >
                  <option value={5}>5</option>
                  <option value={15}>15</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Клиент
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Сумма
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Статус
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Дата создания
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Действия
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {orders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      #{order.id}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{order.fio}</div>
                        <div className="text-sm text-gray-500">{order.phone}</div>
                        {order.email && (
                          <div className="text-sm text-gray-500">{order.email}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {order.totalPrice.toLocaleString('ru-RU')} ₽
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <select
                        value={order.status}
                        onChange={(e) => handleUpdateStatus(order.id, e.target.value)}
                        disabled={updatingOrder === order.id}
                        className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="New">Новый</option>
                        <option value="Processing">В обработке</option>
                        <option value="Shipping">Отправлен</option>
                        <option value="Delivered">Доставлен</option>
                        <option value="Cancelled">Отменен</option>
                      </select>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {new Date(order.created).toLocaleDateString('ru-RU')}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => {
                          setSelectedOrderId(order.id);
                          setShowItemsModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Позиции
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Пагинация */}
          {pagination.totalPages > 1 && (
            <div className="px-6 py-3 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Показано {((pagination.page - 1) * pagination.pageSize) + 1} - {Math.min(pagination.page * pagination.pageSize, pagination.totalCount)} из {pagination.totalCount}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(pagination.page - 1)}
                    disabled={!pagination.hasPreviousPage}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className="text-sm text-gray-700">
                    Страница {pagination.page} из {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(pagination.page + 1)}
                    disabled={!pagination.hasNextPage}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Модальное окно создания заказа */}
      <CreateOrderModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateOrderSuccess}
      />
      {/* Модальное окно позиций заказа */}
      <OrderItemsModal
        isOpen={showItemsModal}
        onClose={() => setShowItemsModal(false)}
        orderId={selectedOrderId || 0}
      />
    </>
  );
}
